import { useEffect, useRef, useState } from 'react'
import firebase from '../../firebase/compat'
import Location from '../../hooks/Location'
import putFileWithProgress from '../../utils/putFileWithProgress'
import { STORYTELLING_KIND_LABELS } from '../../helpers/storytellingHelpers/storytellingKinds'
import StorytellingMediaPreview from '../../components/storytelling/StorytellingMediaPreview'
import CloseIcon from '@mui/icons-material/Close'

// De openbare pagina achter een storytelling-deellink: zonder inlog een foto
// of video, een quote of een anekdote insturen. Alles gaat via de callables,
// geen Firestore (ADR-022). De organisatie komt uit de link op de server; de
// organisatie in de url wordt niet gebruikt en niet meegestuurd.
//
// De pagina valt dicht: zolang `getStorytellingLink` niet gelukt is, staat er
// geen formulier en geen organisatienaam.
// AI-CONTEXT: See docs/features/storytelling-deellink.md
// AI-CONTEXT: See docs/pitfalls.md#security-public-page-reads-its-own-gates
//
// Geen LayoutOpen: die tekent Topbar, en Topbar leest Firestore met de
// organisatie uit de url.

/**
 * @typedef {object} StorytellingLimits
 * @property {number} quoteChars
 * @property {number} anecdoteChars
 * @property {number} nameChars
 * @property {number} imageBytes
 * @property {number} videoBytes
 * @property {Array<string>} imageTypes
 * @property {Array<string>} videoTypes
 */

/**
 * @typedef {object} OpenLink
 * @property {Array<'media'|'quote'|'anecdote'>} types
 * @property {{CommunityName: string, Logo: string}} branding
 * @property {StorytellingLimits} limits
 */

/**
 * @typedef {object} ChosenFile
 * @property {number} key - Vast per gekozen bestand, ook als er een wegvalt
 * @property {File} file
 * @property {string} url - Object-url voor het voorbeeld; opruimen met URL.revokeObjectURL
 */

const MB = 1024 * 1024
// Elk bestand wordt een eigen inzending, en de server staat per afzender 10
// inzendingen per uur toe (LIMITS_PER_HOUR in submitStorytelling.js). Vijf
// tegelijk laat ruimte voor een tweede keer.
const MAX_FILES = 5

const INACTIVE = 'Deze link is niet meer actief.'
const LOAD_ERROR = 'Deze pagina kon niet geladen worden. Probeer het later nog eens.'
const GENERIC_ERROR = 'Er ging iets mis. Probeer het later nog eens.'
const UPLOAD_FAILED = 'Het uploaden is mislukt. Controleer je verbinding en probeer het opnieuw.'
const TOO_MANY = `Je kunt maximaal ${MAX_FILES} bestanden tegelijk insturen.`

/** @param {string} name */
const callable = (name) => firebase.app().functions('europe-west1').httpsCallable(name)

/**
 * De melding bij een reden van de server (of van de controle vooraf).
 * @param {string} reason
 * @param {StorytellingLimits} limits
 * @returns {string}
 */
const reasonMessage = (reason, limits) => {
    switch (reason) {
        case 'file-type':
            return 'Dit soort bestand kan niet. Kies een foto (jpg of png) of een video (mp4 of mov).'
        case 'file-too-large':
            return `Dit bestand is te groot. Een foto mag maximaal ${Math.round(limits.imageBytes / MB)} MB zijn, een video ${Math.round(limits.videoBytes / MB)} MB.`
        case 'file-missing':
        case 'file-size-invalid':
            return UPLOAD_FAILED
        case 'consent-missing':
            return 'Zet eerst het vinkje voor toestemming.'
        case 'text-missing':
            return 'Vul eerst iets in.'
        case 'text-too-long':
            return 'De tekst is te lang.'
        case 'name-too-long':
            return 'De naam is te lang.'
        default:
            return GENERIC_ERROR
    }
}

/**
 * Controle vooraf, met de grenzen van de server: een fout bestand wordt niet eens geüpload.
 * @param {File} file
 * @param {StorytellingLimits} limits
 * @returns {string} Reden, of '' als het bestand mag
 */
const fileProblem = (file, limits) => {
    const isVideo = limits.videoTypes.includes(file.type)
    if (!isVideo && !limits.imageTypes.includes(file.type)) return 'file-type'
    return file.size > (isVideo ? limits.videoBytes : limits.imageBytes) ? 'file-too-large' : ''
}

/**
 * @param {object} props - Geen; de code komt uit de url (/:client/storytellingopen/:code/open)
 */
const StorytellingOpen = () => {
    const code = Location()[3] || ''

    // null = laden, OpenLink = actief, 'inactive' / 'error' = geen formulier.
    const [link, setLink] = useState(/** @type {OpenLink | 'inactive' | 'error' | null} */ (null))
    const [kind, setKind] = useState('')
    const [text, setText] = useState('')
    const [name, setName] = useState('')
    const [files, setFiles] = useState(/** @type {Array<ChosenFile>} */ ([]))
    const nextKey = useRef(0)
    const [consent, setConsent] = useState(false)
    const [website, setWebsite] = useState('')
    const [saving, setSaving] = useState(false)
    const [progress, setProgress] = useState(/** @type {number | null} */ (null))
    // Welk bestand er geüpload wordt, alleen bij meer dan één.
    const [step, setStep] = useState(/** @type {{n: number, total: number} | null} */ (null))
    const [problem, setProblem] = useState('')
    const [sent, setSent] = useState(false)

    // De voorbeelden houden het bestand in het geheugen tot hun url opgeruimd is.
    const filesRef = useRef(files)
    filesRef.current = files
    useEffect(() => () => filesRef.current.forEach((f) => URL.revokeObjectURL(f.url)), [])

    useEffect(() => {
        let cancelled = false
        callable('getStorytellingLink')({ code })
            .then(({ data }) => {
                if (cancelled) return
                setLink(data)
                if (data.types.length === 1) setKind(data.types[0])
            })
            .catch((error) => {
                if (cancelled) return
                if (error?.code === 'not-found') { setLink('inactive'); return }
                console.error('StorytellingOpen: link laden mislukt', error)
                setLink('error')
            })
        return () => { cancelled = true }
    }, [code])

    if (link === null) return <div className="storytelling-open" aria-busy="true" />
    if (link === 'inactive' || link === 'error') {
        return (
            <div className="storytelling-open">
                <p className="storytelling-open-closed">{link === 'inactive' ? INACTIVE : LOAD_ERROR}</p>
            </div>
        )
    }

    const { types, branding, limits } = link
    const orgName = branding.CommunityName || ''

    // De gekozen soort blijft staan: wie nog iets instuurt, doet vaak hetzelfde nog eens.
    const resetForm = () => {
        setText('')
        setName('')
        files.forEach((f) => URL.revokeObjectURL(f.url))
        setFiles([])
        setConsent(false)
        setProgress(null)
        setProblem('')
        setSent(false)
    }

    /**
     * Gekozen bestanden komen bij de lijst; een fout bestand niet, met de reden erbij.
     * @param {React.ChangeEvent<HTMLInputElement>} e
     */
    const fileChangeHandler = (e) => {
        const chosen = Array.from(e.target.files || [])
        // Leeg, zodat hetzelfde bestand na weghalen opnieuw te kiezen is.
        e.target.value = ''
        const reasons = chosen.map((f) => fileProblem(f, limits))
        const allowed = chosen.filter((f, i) => !reasons[i])
        const room = MAX_FILES - files.length
        const added = allowed.slice(0, Math.max(room, 0)).map((f) => {
            nextKey.current += 1
            return { key: nextKey.current, file: f, url: URL.createObjectURL(f) }
        })
        const reason = reasons.find(Boolean)
        setProblem(reason ? reasonMessage(reason, limits) : allowed.length > added.length ? TOO_MANY : '')
        setFiles([...files, ...added])
    }

    /** @param {number} key */
    const removeFile = (key) => {
        setFiles((prev) => {
            prev.filter((f) => f.key === key).forEach((f) => URL.revokeObjectURL(f.url))
            return prev.filter((f) => f.key !== key)
        })
    }

    /**
     * @param {*} error - Fout van een callable
     * @param {string} [prefix] - Wat er al wel verstuurd is
     * @returns {void}
     */
    const showCallableError = (error, prefix = '') => {
        if (error?.code === 'not-found') { setLink('inactive'); return }
        if (error?.code === 'resource-exhausted') { setProblem(prefix + error.message); return }
        const reason = error?.code === 'invalid-argument' && error.details?.reasons?.[0]
        if (!reason) console.error('StorytellingOpen: insturen mislukt', error)
        setProblem(prefix + reasonMessage(reason || '', limits))
    }

    /**
     * Eén bestand: uploadlink, uploaden, insturen. Een mislukte upload geeft
     * 'upload-failed'; dan staat er voor dat bestand niets in de wachtrij.
     * @param {File} file
     */
    const sendFile = async (file) => {
        const { data: upload } = await callable('requestStorytellingUpload')({ code, contentType: file.type, size: file.size })
        setProgress(0)
        try {
            await putFileWithProgress(upload.uploadUrl, file, upload.headers, setProgress)
        } catch (error) {
            console.error('StorytellingOpen: uploaden mislukt', error)
            throw Object.assign(new Error('upload-failed'), { uploadFailed: true })
        }
        await callable('submitStorytelling')({ code, kind, storagePath: upload.storagePath, consent: true, website })
    }

    // Elk bestand is een eigen inzending. Wat verstuurd is gaat uit de lijst,
    // zodat 'Versturen' na een fout alleen de rest nog eens probeert.
    const sendFiles = async () => {
        const queue = files
        for (let i = 0; i < queue.length; i += 1) {
            setStep(queue.length > 1 ? { n: i + 1, total: queue.length } : null)
            try {
                await sendFile(queue[i].file)
            } catch (error) {
                const prefix = i > 0 ? `${i} van de ${queue.length} is verstuurd. ` : ''
                if (error.uploadFailed) setProblem(prefix + UPLOAD_FAILED)
                else showCallableError(error, prefix)
                return false
            }
            removeFile(queue[i].key)
        }
        return true
    }

    const send = async () => {
        if (saving) return
        setSaving(true)
        setProblem('')
        if (kind === 'media') {
            if (await sendFiles()) setSent(true)
        } else {
            try {
                await callable('submitStorytelling')({ code, kind, text: text.trim(), name: name.trim(), website })
                setSent(true)
            } catch (error) {
                showCallableError(error)
            }
        }
        setProgress(null)
        setStep(null)
        setSaving(false)
    }

    /** @param {React.FormEvent} e */
    const submitHandler = (e) => {
        e.preventDefault()
        send()
    }

    const canSend = kind === 'media' ? files.length > 0 && consent : Boolean(kind) && Boolean(text.trim())

    return (
        <div className="storytelling-open">
            <header className="storytelling-open-header">
                {branding.Logo && <img className="storytelling-open-logo" src={branding.Logo} alt={`Logo ${orgName}`.trim()} />}
                <h1>{orgName ? `Deel je verhaal met ${orgName}` : 'Deel je verhaal'}</h1>
            </header>

            {sent ? (
                <div className="storytelling-open-thanks">
                    <p>Bedankt! We hebben je inzending ontvangen. Het team bekijkt hem voordat hij gebruikt wordt.</p>
                    <button type="button" onClick={resetForm}>Nog iets insturen</button>
                </div>
            ) : (
                <form className="storytelling-open-form" onSubmit={submitHandler} noValidate>
                    {types.length > 1 && (
                        <fieldset className="storytelling-open-kinds">
                            <legend>Wat wil je insturen?</legend>
                            {types.map((t) => (
                                <label key={t}>
                                    <input type="radio" name="kind" value={t} checked={kind === t} onChange={() => { setKind(t); setProblem('') }} />
                                    {STORYTELLING_KIND_LABELS[t]}
                                </label>
                            ))}
                        </fieldset>
                    )}

                    {(kind === 'quote' || kind === 'anecdote') && (
                        <label className="storytelling-open-field">
                            <span>{kind === 'quote' ? 'Je quote' : 'Je verhaal'}</span>
                            <textarea
                                value={text}
                                maxLength={kind === 'quote' ? limits.quoteChars : limits.anecdoteChars}
                                rows={kind === 'quote' ? 4 : 10}
                                onChange={(e) => setText(e.target.value)}
                            />
                        </label>
                    )}

                    {kind === 'media' && (
                        <>
                            <label className="storytelling-open-field">
                                <span>Kies foto&apos;s of video&apos;s</span>
                                <input type="file" multiple disabled={saving} accept={[...limits.imageTypes, ...limits.videoTypes].join(',')} onChange={fileChangeHandler} />
                            </label>
                            <label className="storytelling-open-consent">
                                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
                                <span>
                                    {`Ik geef ${orgName || 'deze organisatie'} toestemming om ${files.length > 1 ? "deze foto's of video's" : 'deze foto of video'} te gebruiken, en iedereen die erop staat is het daarmee eens.`}
                                </span>
                            </label>
                        </>
                    )}

                    {/* Alleen bij een quote of anekdote: bij een foto of video bewaart het goedgekeurde item geen naam. */}
                    {(kind === 'quote' || kind === 'anecdote') && (
                        <label className="storytelling-open-field">
                            <span>Je naam (niet verplicht)</span>
                            <input type="text" value={name} maxLength={limits.nameChars} autoComplete="name" onChange={(e) => setName(e.target.value)} />
                        </label>
                    )}

                    {/* Lokveld: buiten beeld, niet in de tabvolgorde, niet voor schermlezers. */}
                    <input
                        className="intake-honeypot"
                        type="text"
                        name="website"
                        data-testid="storytelling-open-honeypot"
                        value={website}
                        onChange={(e) => setWebsite(e.target.value)}
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                    />

                    {kind === 'media' && files.length > 0 && (
                        <ul className="storytelling-open-previews" aria-label="Gekozen bestanden">
                            {files.map((f) => (
                                <li key={f.key} className="storytelling-open-preview">
                                    <StorytellingMediaPreview src={f.url} type={f.file.type} alt={f.file.name} />
                                    <button type="button" className="storytelling-open-preview-remove" disabled={saving} aria-label={`${f.file.name} weghalen`} title="Weghalen" onClick={() => removeFile(f.key)}>
                                        <CloseIcon aria-hidden="true" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}

                    {progress !== null && (
                        <div className="storytelling-open-progress">
                            <progress value={progress} max={100} />
                            <span>{step ? `Uploaden ${step.n} van ${step.total}: ${progress}%` : `Uploaden: ${progress}%`}</span>
                        </div>
                    )}
                    {problem && <p className="storytelling-open-problem" role="alert">{problem}</p>}

                    {kind && (
                        <div className="event-open-actions">
                            <button type="submit" disabled={saving || !canSend}>{saving ? 'Bezig met versturen…' : 'Versturen'}</button>
                        </div>
                    )}
                </form>
            )}
        </div>
    )
}

export default StorytellingOpen
